import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/marketing/BrandMark";
import { api, inputCls, labelCls, btnPrimary } from "@/lib/api";

const Login = ({ mode = "signin" }) => {
  const navigate = useNavigate();
  const isSignup = mode === "signup";
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = isSignup
        ? await api("/auth/signup", { method: "POST", body: JSON.stringify({ email, password, first_name: firstName }) })
        : await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      const { business } = await api("/business/me");
      navigate(business ? "/app" : "/onboarding", { state: { user } });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5] px-5 py-12 font-sans" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2" data-testid="login-brand-link">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-guinep-soft">
            <BrandMark size={16} />
          </span>
          <span className="font-serif text-xl font-semibold tracking-tight text-guinep-ink">Guinep Seed.</span>
        </Link>
        <p className="mt-1.5 text-sm text-guinep-mute">From social discovery to client action.</p>

        <h1 className="mt-10 font-serif text-3xl font-medium tracking-tight text-guinep-ink">
          {isSignup ? "Create your page" : "Sign in"}
        </h1>
        <p className="mt-1.5 text-sm text-guinep-mute">
          {isSignup ? "Start your Guinep Seed page." : "Welcome back — manage your business page."}
        </p>

        <button
          data-testid="google-signin-button"
          onClick={googleSignIn}
          className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-xl border border-guinep-line bg-white py-3.5 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft"
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2.05 3.72-5.07 3.72-8.6z"/>
            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.06 7.9-2.9l-3.75-2.9c-1 .7-2.36 1.2-4.15 1.2-3.15 0-5.82-2.1-6.78-5l-.14.01-3.63 2.8-.05.13C3.36 21.3 7.31 24 12 24z"/>
            <path fill="#FBBC05" d="M5.22 14.4c-.25-.73-.4-1.51-.4-2.4s.15-1.67.4-2.4l-.01-.16-3.68-2.85-.12.06C.5 8.5 0 10.2 0 12s.5 3.5 1.4 5.35l3.82-2.95z"/>
            <path fill="#EA4335" d="M12 4.6c2.26 0 3.79.98 4.66 1.8l3.4-3.3C18.1 1.14 15.2 0 12 0 7.31 0 3.36 2.7 1.4 6.65l3.82 2.95C6.18 6.7 8.85 4.6 12 4.6z"/>
          </svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-guinep-line" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-guinep-mute/70">Or with email</span>
          <span className="h-px flex-1 bg-guinep-line" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isSignup && (
            <div>
              <label className={labelCls} htmlFor="first-name">First name</label>
              <input id="first-name" data-testid="first-name-input" className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Maya" required />
            </div>
          )}
          <div>
            <label className={labelCls} htmlFor="email">Email address</label>
            <input id="email" data-testid="email-input" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
          </div>
          <div>
            <label className={labelCls} htmlFor="password">Password</label>
            <input id="password" data-testid="password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isSignup ? "8+ characters" : "••••••••"} required />
          </div>
          {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
          <button data-testid="auth-submit-button" type="submit" disabled={busy} className={btnPrimary}>
            {isSignup ? "Create account" : "Sign in"}
            <ArrowRight size={16} />
          </button>
        </form>

        <p className="mt-7 text-center text-sm text-guinep-mute">
          {isSignup ? (
            <>Already have a page?{" "}
              <Link to="/login" data-testid="switch-to-signin" className="font-semibold text-guinep-mid hover:text-guinep-deep">Sign in</Link>
            </>
          ) : (
            <>New to Guinep Seed?{" "}
              <Link to="/signup" data-testid="switch-to-signup" className="font-semibold text-guinep-mid hover:text-guinep-deep">Create one</Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default Login;
